/**
 * 69.45, the support role (DECISIONS 256 (1); 268 (3), the owner's "support leans"; design_sheets/specialists.html): with
 * GameConfig.supportRole and ammo reserves on, a squad's support carries an ammunition case on a pack frame — SUPPORT.case
 * magazines, a life's (absent = full, as the bandages). It gives them out two ways:
 * - **in reach, always:** every SUPPORT.handEveryS seconds a support on its feet hands one magazine to the teammate within
 *   SUPPORT.handReach people shortest of spares (itself among them) — of the gun they are shortest for, the rounds a crate's
 *   magazine would give (core/supply.ts supplyNeeds);
 * - **the drop, for a hold:** `caseDrop` sets the case down open at its feet — a structure (`ammoCase`) with the case's
 *   magazines, a crate as any other (SUPPLY.reach; 242: open to all) until it is emptied or the support picks it up again in
 *   reach (`caseDrop` once more) — then spent (kept at no health, so its id is never minted again). Broken, its magazines are lost.
 * It is refilled at a dump: asking (`resupply`) in reach of a crate that is not a case, it takes the case's shortfall off the
 * crate's magazines, so a support makes no ammunition — it carries a dump's forward. A refill lets go of a case left down
 * (it stays where it lies, a crate).
 *
 * Who is a support: a `support` squad slot (65.34's `+support`), else — the switch's issue (issueSupports, at the setup) —
 * each squad of three or more without one names its last plain rifleman. Pure; the Match steps it (after the restocks).
 */
import type { GameConfig, GameState, PlayerState, StructureState, GroundWeaponKind, Vec3 } from '../types';
import { SUPPORT, SUPPLY } from '../constants';
import { PERSON_BODY_HEIGHT, personSetOf, type PersonSet } from './scaleProfile';
import { supplyNeeds, supplyReach, crateStocked } from './supply';
import { reserveLeft, weaponSpec } from './weapons';
import { apart } from './medical';
import { arcDistance } from './gameState';
import { structureHealth } from './body';
import { vecNormalize, vecSub, vecScale, vecDot, vecLength, vecAdd, surfaceTangentFrame } from '../math';

/** Is the support role in play: the switch, with ammo reserves (without them nobody is short). */
export function supportOn(config: Pick<GameConfig, 'supportRole' | 'ammoReserves'>): boolean {
  return config.supportRole === true && config.ammoReserves === true;
}

/** Is `p` a support carrying a case in this match. */
export function isSupport(state: Pick<GameState, 'config'>, p: Pick<PlayerState, 'role'>): boolean {
  return p.role === 'support' && supportOn(state.config);
}

/** Magazines in the support's case (absent = full; none while it is set down). */
export function caseLeft(p: Pick<PlayerState, 'ammoCase' | 'caseDown'>): number {
  return p.caseDown !== undefined ? 0 : p.ammoCase ?? SUPPORT.case;
}

/** A respawn's case: full again, nothing left down (keys only on a unit that had either, so the others' state is as it was). */
export function freshCase(p: Pick<PlayerState, 'ammoCase' | 'caseDown'>): Pick<PlayerState, 'ammoCase' | 'caseDown'> {
  return p.ammoCase !== undefined || p.caseDown !== undefined ? { ammoCase: undefined, caseDown: undefined } : {};
}

/** A case set down: an `ammoCase` structure. */
export function isCase(s: Pick<StructureState, 'type'>): boolean {
  return s.type === 'ammoCase';
}

/** A dump a support refills at: a stocked crate with magazines that is not a case. */
export function refillsCase(s: StructureState): boolean {
  return !isCase(s) && crateStocked(s) && s.supply!.magazines > 0;
}

/** The world units of `people` at this scale. */
const people = (state: Pick<GameState, 'config'>, n: number) => n * PERSON_BODY_HEIGHT * personSetOf(state).k;

/** Each squad's support: its `support` slot, else (with the switch) its last plain rifleman (rifle, no secondary, no role) — squads of three or more. */
export function issueSupports(state: GameState): GameState {
  if (!supportOn(state.config) || !state.squads?.length) return state;
  const named = new Set<number>();
  for (const sq of state.squads) {
    if (sq.unitIds.length < 3) continue;
    const ms = sq.unitIds.map(id => state.players.find(p => p.id === id)).filter((p): p is PlayerState => !!p);
    if (ms.some(p => p.role === 'support')) continue;
    const plain = ms.filter(p => (p.primary ?? 'rifle') === 'rifle' && !p.secondary && !p.role && p.id !== sq.leader);
    const pick = plain[plain.length - 1];
    if (pick) named.add(pick.id);
  }
  if (!named.size) return state;
  return { ...state, players: state.players.map(p => (named.has(p.id) ? { ...p, role: 'support' as const } : p)) };
}

type Event =
  | { kind: 'caseHanded'; unitId: number; by: number; weapon: GroundWeaponKind; rounds: number }
  | { kind: 'caseDown'; unitId: number; structureId: number; magazines: number }
  | { kind: 'caseUp'; unitId: number; structureId: number; magazines: number }
  | { kind: 'caseFilled'; unitId: number; structureId: number; magazines: number };
export type SupportEvent = Event;

/** On its feet and out of any seat. */
const grounded = (p: PlayerState) => p.alive && !p.airborne && !p.mount;

/** The weapon `q` is shortest of spares for, in magazines, and the rounds a magazine of it gives (null: short of none the crates serve). */
function shortest(q: PlayerState, ps: PersonSet, config: GameConfig): { weapon: GroundWeaponKind; rounds: number; mags: number } | null {
  const need = supplyNeeds(q, ps, config);
  let best: { weapon: GroundWeaponKind; rounds: number; mags: number } | null = null;
  for (const [k, short] of Object.entries(need.byWeapon) as Array<[GroundWeaponKind, number]>) {
    if (!(short > 0)) continue;
    const spec = weaponSpec(ps, k, config), mags = short / spec.magazine;
    if (!best || mags > best.mags) best = { weapon: k, rounds: Math.min(short, spec.magazine), mags };
  }
  return best;
}

/**
 * One beat of the hand-outs (the Match calls it every SUPPORT.handEveryS seconds): each support on its feet with magazines in
 * its case hands one to the teammate in reach shortest of them (the most magazines short; the lower id on a tie). Null when
 * nothing changed.
 */
export function stepHandOuts(state: Pick<GameState, 'players' | 'planets' | 'config'>, ps: PersonSet): { players: PlayerState[]; events: SupportEvent[] } | null {
  if (!supportOn(state.config)) return null;
  let players: PlayerState[] | null = null;
  const events: SupportEvent[] = [];
  const reach = people(state, SUPPORT.handReach);
  for (let i = 0; i < state.players.length; i++) {
    const sup = (players ?? state.players)[i];
    if (sup.role !== 'support' || !grounded(sup) || caseLeft(sup) <= 0) continue;
    let to = -1, give: ReturnType<typeof shortest> = null;
    const all = players ?? state.players;
    for (let j = 0; j < all.length; j++) {
      const q = all[j];
      if (q.teamIndex !== sup.teamIndex || !grounded(q) || (j !== i && apart(sup, q, state.planets) > reach)) continue;
      const s = shortest(q, ps, state.config);
      if (s && (!give || s.mags > give.mags)) { give = s; to = j; }
    }
    if (!give) continue;
    players ??= state.players.slice();
    const q = players[to], spec = weaponSpec(ps, give.weapon, state.config);
    players[to] = { ...q, reserves: { ...(q.reserves ?? {}), [give.weapon]: reserveLeft(q, give.weapon, spec) + give.rounds } };
    const s = players[i];
    players[i] = { ...s, ammoCase: caseLeft(s) - 1 };
    events.push({ kind: 'caseHanded', unitId: q.id, by: s.id, weapon: give.weapon, rounds: give.rounds });
  }
  return players ? { players, events } : null;
}

/** Where a case set down lies: at the support's feet, a little ahead of it (along its heading on the ground). */
function caseSpot(p: PlayerState, state: Pick<GameState, 'planets' | 'config'>): Vec3 {
  const n = vecNormalize(p.surfaceDir), radius = state.planets[p.planetIndex]?.radius ?? 1;
  const along = p.heading ? vecSub(p.heading, vecScale(n, vecDot(p.heading, n))) : surfaceTangentFrame(n).tx;
  const ahead = vecLength(along) > 1e-12 ? vecNormalize(along) : surfaceTangentFrame(n).tx;
  return vecNormalize(vecAdd(n, vecScale(ahead, people(state, 0.4) / radius)));
}

/**
 * One tick's cases set down and picked up, from each unit's `caseDrop`: a support whose own case stands in reach picks it up
 * (its magazines back in the case); else, with magazines in its case, on its feet, it sets the case down open. Null when
 * nothing changed.
 */
export function stepCases(state: Pick<GameState, 'players' | 'structures' | 'planets' | 'config'>, asks: ReadonlyArray<boolean | undefined>): { players: PlayerState[]; structures: StructureState[]; events: SupportEvent[] } | null {
  if (!supportOn(state.config)) return null;
  let players: PlayerState[] | null = null, structures: StructureState[] | null = null;
  const events: SupportEvent[] = [];
  for (let i = 0; i < state.players.length; i++) {
    const p = (players ?? state.players)[i];
    if (!asks[i] || p.role !== 'support' || !grounded(p)) continue;
    const all: StructureState[] = structures ?? state.structures;
    const own = p.caseDown !== undefined ? all.find(s => s.id === p.caseDown && s.health > 0) : undefined;
    const planet = state.planets[p.planetIndex];
    if (own && planet && own.planetIndex === p.planetIndex && arcDistance(p.surfaceDir, own.surfaceDir, planet.radius) <= supplyReach(state)) {
      const mags = Math.min(SUPPORT.case, own.supply?.magazines ?? 0);
      structures = all.map(s => (s.id === own.id ? spent(s) : s));   // (kept, spent: its id never minted again — the page keys its meshes by id)
      (players ??= state.players.slice())[i] = { ...p, ammoCase: mags, caseDown: undefined };
      events.push({ kind: 'caseUp', unitId: p.id, structureId: own.id, magazines: mags });
      continue;
    }
    const mags = caseLeft(p);
    if (mags <= 0) continue;
    const id = all.reduce((m, s) => Math.max(m, s.id), -1) + 1;
    const placed: StructureState = { id, type: 'ammoCase', planetIndex: p.planetIndex, surfaceDir: caseSpot(p, state), health: structureHealth('ammoCase'), ownerId: p.id, supply: { teamIndex: p.teamIndex, bandages: 0, magazines: mags } };
    structures = [...all, placed];
    (players ??= state.players.slice())[i] = { ...p, ammoCase: 0, caseDown: id };
    events.push({ kind: 'caseDown', unitId: p.id, structureId: id, magazines: mags });
  }
  return players ? { players, structures: structures!, events } : null;
}

/**
 * One tick's case refills: each support asking (`resupply`) with its case short, in reach of a dump (a crate with magazines,
 * not a case; 242: any side's), takes the shortfall off the dump's magazines; a case it had left down is let go. Null when
 * nothing changed.
 */
export function stepCaseRefills(state: Pick<GameState, 'players' | 'structures' | 'planets' | 'config' | 'modeState'>, asks: ReadonlyArray<boolean | undefined>): { players: PlayerState[]; structures: StructureState[]; events: SupportEvent[] } | null {
  if (!supportOn(state.config)) return null;
  let players: PlayerState[] | null = null, structures: StructureState[] | null = null;
  const events: SupportEvent[] = [];
  for (let i = 0; i < state.players.length; i++) {
    const p = (players ?? state.players)[i];
    if (!asks[i] || p.role !== 'support' || !grounded(p)) continue;
    const short = SUPPORT.case - caseLeft(p);
    if (short <= 0) continue;
    const all: StructureState[] = structures ?? state.structures;
    const dump = nearestDump({ ...state, structures: all }, p, supplyReach(state));
    if (!dump) continue;
    const take = Math.min(short, dump.supply!.magazines);
    structures = all.map(s => (s.id === dump.id ? { ...s, supply: { ...s.supply!, magazines: s.supply!.magazines - take } } : s));
    (players ??= state.players.slice())[i] = { ...p, ammoCase: caseLeft(p) + take, caseDown: undefined };
    events.push({ kind: 'caseFilled', unitId: p.id, structureId: dump.id, magazines: take });
  }
  return players ? { players, structures: structures!, events } : null;
}

/** The nearest dump a support refills at within `maxArc` (world units) of `p` on its planet, any side's (242), or null. */
export function nearestDump(state: Pick<GameState, 'structures' | 'planets'>, p: Pick<PlayerState, 'planetIndex' | 'surfaceDir'>, maxArc: number): StructureState | null {
  const planet = state.planets[p.planetIndex];
  if (!planet) return null;
  let best: StructureState | null = null, bestD = Infinity;
  for (const s of state.structures) {
    if (s.planetIndex !== p.planetIndex || !refillsCase(s)) continue;
    const d = arcDistance(p.surfaceDir, s.surfaceDir, planet.radius);
    if (d <= maxArc && d < bestD) { bestD = d; best = s; }
  }
  return best;
}

/** A case picked up or emptied: kept in the state, spent (no health, no magazines) — so its id is never minted again. */
function spent(s: StructureState): StructureState {
  return { ...s, health: 0, supply: { ...s.supply!, magazines: 0 } };
}

/** The cases emptied, spent; the supports whose case is spent or broken let go of it. Null when nothing changed. */
export function clearCases(state: Pick<GameState, 'players' | 'structures'>): { players: PlayerState[]; structures: StructureState[] } | null {
  const emptied = state.structures.some(s => isCase(s) && s.health > 0 && (s.supply?.magazines ?? 0) <= 0);
  const structures = emptied ? state.structures.map(s => (isCase(s) && s.health > 0 && (s.supply?.magazines ?? 0) <= 0 ? spent(s) : s)) : state.structures;
  const dead = (id: number) => { const s = structures.find(x => x.id === id); return !s || s.health <= 0; };
  const letGo = state.players.some(p => p.caseDown !== undefined && dead(p.caseDown));
  if (!emptied && !letGo) return null;
  return { structures, players: letGo ? state.players.map(p => (p.caseDown !== undefined && dead(p.caseDown) ? { ...p, caseDown: undefined } : p)) : state.players };
}

/** The bots' reach for a support's walk to a low teammate, and a hold's, in world units. */
export function supportWalk(state: Pick<GameState, 'config'>): number { return people(state, SUPPORT.walk); }
export function dropNear(state: Pick<GameState, 'config'>): number { return people(state, SUPPORT.dropNear); }
/** The case's own reach for a hand-out, in world units. */
export function handReach(state: Pick<GameState, 'config'>): number { return people(state, SUPPORT.handReach); }
/** Under this many magazines a bot support goes for a dump. */
export const CASE_LOW = Math.ceil(SUPPORT.case * SUPPORT.refillShare);
/** SUPPLY.walk in world units (the bots' short walk to a crate of any side). */
export function shortWalk(state: Pick<GameState, 'config'>): number { return people(state, SUPPLY.walk); }
